import { useRef, useState } from "react";
import Nav from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Camera, Check } from "lucide-react";
import { StyleTag } from "./Signup";
import MemoryPanel from "../components/MemoryPanel";
import FeedbackCard from "../components/FeedbackCard";
import WeeklyRecapCard from "../components/WeeklyRecapCard";
import InviteCard from "../components/InviteCard";

const ZAF_PREFS = [
  { key: "gentle", label: "Keep it gentle with me" },
  { key: "straight", label: "Talk straight — no sugar-coating" },
  { key: "humour", label: "A bit of humour is welcome" },
  { key: "short", label: "Keep replies short and simple" },
  { key: "ask", label: "Ask me questions to draw me out" },
  { key: "listen", label: "Just listen — don't rush to fix things" },
  { key: "easy_when_low", label: "Go easy on me when I'm having a low day" },
  { key: "no_pressure", label: "Never pressure me to talk" },
];

const EMPATHY_LABELS = { 1: "Straight-talking", 2: "Fairly direct", 3: "Balanced", 4: "Warm", 5: "Very gentle" };

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    mobile: user?.mobile || "",
    address: user?.address || "",
    suburb: user?.suburb || "",
    state: user?.state || "",
    postcode: user?.postcode || "",
    country: user?.country || "",
    about_me: user?.about_me || "",
    emergency_contact_name: user?.emergency_contact_name || "",
    emergency_contact_email: user?.emergency_contact_email || "",
    emergency_contact_phone: user?.emergency_contact_phone || "",
    zaf_prefs: user?.zaf_prefs || [],
    empathy_level: user?.empathy_level ?? 3,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const togglePref = (key) =>
    setForm((f) => ({
      ...f,
      zaf_prefs: f.zaf_prefs.includes(key)
        ? f.zaf_prefs.filter((p) => p !== key)
        : [...f.zaf_prefs, key],
    }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      updateUser(data.user);
      toast.success("Saved");
    } catch (err) {
      toast.error("Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: p } = await api.put("/profile", { photo_path: data.path });
      updateUser(p.user);
      toast.success("Photo updated");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7F6]">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-display text-4xl text-slate-800">Your profile</h1>
        <p className="text-slate-600 mt-1">The more Zaf knows, the more they can be themselves with you.</p>

        <section className="mt-8 bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-[#CDE7E3] flex items-center justify-center text-slate-600 font-display text-3xl" data-testid="profile-avatar">
                {user?.photo_path ? (
                  <img src={fileUrl(user.photo_path)} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  user?.first_name?.[0]?.toUpperCase() || "?"
                )}
              </div>
              <button
                onClick={() => fileRef.current?.click()}
                className="absolute bottom-0 right-0 w-8 h-8 bg-[#2E9D96] hover:bg-[#1F7B75] text-white rounded-full flex items-center justify-center shadow-md transition-colors"
                data-testid="profile-avatar-btn"
                aria-label="Change photo"
                disabled={uploading}
              >
                <Camera size={14} />
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadAvatar(e.target.files[0])} />
            </div>
            <div>
              <p className="font-display text-2xl text-slate-800">{user?.first_name} {user?.last_name}</p>
              <p className="text-slate-500 text-sm">{user?.email}</p>
              <p className="text-slate-500 text-xs mt-1 uppercase tracking-[0.15em] font-semibold">
                {user?.subscription_status === "trialing" ? "3-day free trial" : user?.subscription_status}
              </p>
            </div>
          </div>
        </section>

        <form onSubmit={save} className="mt-6 space-y-6">
          <section className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-5">
            <h2 className="font-display text-2xl text-slate-800">About you</h2>
            <label className="block">
              <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Bio / for Zaf's memory</span>
              <textarea rows={4} value={form.about_me} onChange={on("about_me")} className="input zaf-focus mt-2" data-testid="profile-about" />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <Field label="First name"><input value={form.first_name} onChange={on("first_name")} className="input zaf-focus" data-testid="profile-first-name" /></Field>
              <Field label="Last name"><input value={form.last_name} onChange={on("last_name")} className="input zaf-focus" data-testid="profile-last-name" /></Field>
              <Field label="Mobile"><input value={form.mobile} onChange={on("mobile")} className="input zaf-focus" /></Field>
              <Field label="Country"><input value={form.country} onChange={on("country")} className="input zaf-focus" /></Field>
              <Field label="Address"><input value={form.address} onChange={on("address")} className="input zaf-focus" /></Field>
              <Field label="Suburb"><input value={form.suburb} onChange={on("suburb")} className="input zaf-focus" /></Field>
              <Field label="State"><input value={form.state} onChange={on("state")} className="input zaf-focus" /></Field>
              <Field label="Postcode"><input value={form.postcode} onChange={on("postcode")} className="input zaf-focus" /></Field>
            </div>
          </section>

          <section className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-5" data-testid="zaf-prefs-section">
            <div>
              <h2 className="font-display text-2xl text-slate-800">How Zaf talks to me</h2>
              <p className="text-slate-500 text-sm mt-1">Tick whatever feels right — no need to overthink it. Change it anytime.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {ZAF_PREFS.map((p) => {
                const active = form.zaf_prefs.includes(p.key);
                return (
                  <button
                    type="button"
                    key={p.key}
                    onClick={() => togglePref(p.key)}
                    data-testid={`zaf-pref-${p.key}`}
                    aria-pressed={active}
                    className={`flex items-center gap-3 text-left rounded-2xl border px-4 py-3.5 transition-all ${
                      active
                        ? "border-[#2E9D96] bg-[#2E9D96]/[0.06] shadow-sm"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <span
                      className={`shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                        active ? "bg-[#2E9D96] border-[#2E9D96] text-white" : "border-slate-300 text-transparent"
                      }`}
                    >
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span className={`text-[15px] leading-snug ${active ? "text-slate-800 font-semibold" : "text-slate-600"}`}>
                      {p.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg text-slate-800">How warm should Zaf be?</h3>
                <span className="text-sm font-semibold text-[#1F7B75]" data-testid="empathy-value">{EMPATHY_LABELS[form.empathy_level]}</span>
              </div>
              <p className="text-slate-500 text-sm mt-1">Zaf starts balanced and adapts to you — nudge it whenever it doesn't feel right.</p>
              <input
                type="range"
                min={1}
                max={5}
                step={1}
                value={form.empathy_level}
                onChange={(e) => setForm({ ...form, empathy_level: Number(e.target.value) })}
                data-testid="empathy-slider"
                className="w-full mt-5 accent-[#2E9D96] cursor-pointer"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1.5">
                <span>Straight-talking mate</span>
                <span>Gentle &amp; soft</span>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-5">
            <h2 className="font-display text-2xl text-slate-800">Trusted contact</h2>
            <p className="text-slate-500 text-sm">When you add someone, we send them a short heads-up that you've chosen them. If you ever go quiet or you're having a hard time, they get a gentle "please check on your friend" — never your chats.</p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name"><input value={form.emergency_contact_name} onChange={on("emergency_contact_name")} className="input zaf-focus" data-testid="profile-ec-name" /></Field>
              <Field label="Email"><input type="email" value={form.emergency_contact_email} onChange={on("emergency_contact_email")} className="input zaf-focus" data-testid="profile-ec-email" /></Field>
              <Field label="Phone (optional)"><input value={form.emergency_contact_phone} onChange={on("emergency_contact_phone")} className="input zaf-focus" /></Field>
            </div>
          </section>

          <button data-testid="profile-save" disabled={saving} className="bg-[#2E9D96] hover:bg-[#1F7B75] disabled:opacity-60 text-white font-semibold rounded-full px-8 py-3 transition-colors">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </form>

        <WeeklyRecapCard />

        <InviteCard />

        <MemoryPanel />

        <FeedbackCard />
      </div>
      <StyleTag />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
